import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {DropdownBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface CrmMappingRowFieldProps {
  iconName?: IconName;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  hasHover?: boolean;
  hasError?: boolean;
  errorMessage?: ReactNode;
  message?: ReactNode;
}

/*
 * Style.
 */

const StyledReadOnlyWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  padding: 6px 8px;
  border-radius: 6px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledMappingTextDiv = styled.div`
  flex: 1;
  ${ellipsis()}
`;

const StyledDropdownBoxContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledErrorMessageWrapperDiv = styled.div`
  margin-bottom: -12px;
  margin-top: 4px;
`;

const StyledMessageWrapperDiv = styled.div`
  margin-bottom: -7px;
  margin-top: 11px;
`;

/*
 * Component.
 */

export const CrmMappingRowField: FC<CrmMappingRowFieldProps> = (props) => {
  const theme = useUserTheme();
  const {iconName, children, isReadOnly, isDisabled, hasHover, hasError, errorMessage, message} = props;
  if (isReadOnly) {
    return renderReadOnly(iconName, children);
  }
  return (
    <DropdownBox
      hasHover={hasHover}
      hasError={hasError}
      noInputMarginRight
      message={maybeRenderMessage(hasError, message)}
      errorMessage={maybeRenderErrorMessage(hasError, errorMessage)}
      isDisabled={isDisabled}
      value={
        <StyledDropdownBoxContentWrapperDiv>
          {iconName && <StyledIcon name={iconName} color={theme.greys.shade70} />}
          <StyledMappingTextDiv>{children}</StyledMappingTextDiv>
        </StyledDropdownBoxContentWrapperDiv>
      }
      boxStyles={{margin: 0, borderRadius: '6px'}}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderMessage(hasError?: boolean, message?: ReactNode) {
  if (hasError || !message) {
    return null;
  }
  return <StyledMessageWrapperDiv>{message}</StyledMessageWrapperDiv>;
}

function maybeRenderErrorMessage(hasError?: boolean, errorMessage?: ReactNode) {
  if (!hasError || !errorMessage) {
    return null;
  }
  return <StyledErrorMessageWrapperDiv>{errorMessage}</StyledErrorMessageWrapperDiv>;
}

function renderReadOnly(iconName: IconName | undefined, children: ReactNode) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledReadOnlyWrapperDiv>
          {iconName && <StyledIcon name={iconName} color={theme.greys.shade70} />}
          <StyledMappingTextDiv>{children}</StyledMappingTextDiv>
        </StyledReadOnlyWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
